import {
	calculateBounds,
	formatPointForDeckGL,
	groupPointsByCluster,
	calculateClusterStats,
	hexToRgb,
} from "@/components/clustering/model/ClusteringUtils.js";
import ClusterList from "@/components/clustering/ClusterList.vue";
import ClusterStatisticsPanel from "@/components/clustering/ClusterStatisticsPanel.vue";
import SelectedPointsPanel from "@/components/clustering/SelectedPointsPanel.vue";
import ClusteringWizard from "@/components/clustering/ClusteringWizard.vue";
import ClusterInfoWindow from "@/components/clustering/ClusterInfoWindow.vue";
import Clustering from "@/components/clustering/model/ClusteringModel.js";
import {createClustering} from "@/components/clustering/model/ClusteringServices.js";
import * as ClusteringServices from "@/components/clustering/model/ClusteringServices.js";
import {generalPurposeMixin} from "@/mixins/generalPurposeMixin.js";
import {GoogleMapsOverlay} from "@deck.gl/google-maps";
import {ScatterplotLayer, PolygonLayer} from "@deck.gl/layers";
import {HeatmapLayer} from "@deck.gl/aggregation-layers";
import convex from "@turf/convex";
import {points as turfPoints} from "@turf/helpers";
import {v4 as uuidv4} from "uuid";
import * as XLSX from "xlsx";

export const clusteringMapMixin = {
	name: "ClusteringMap",
	components: {
		ClusterList,
		ClusterStatisticsPanel,
		SelectedPointsPanel,
		ClusteringWizard,
		ClusterInfoWindow,
	},
	mixins: [generalPurposeMixin],
	data() {
		return {
			// Wizard configuration
			showWizard: false,
			isWizardMinimized: false,
			wizardRef: null,
			isClickCreateMode: false,
			clickCreateListener: null,
			isFabExpanded: false,
			showStatisticsPanel: false,

			// Map configuration
			map: null,
			mapLoaded: false,
			mapCenter: {lat: 4.711, lng: -74.072}, // Bogotá
			mapZoom: 11,

			// deck.gl configuration
			deckOverlay: null,
			deckLayers: [],

			// Clustering data
			currentClustering: new Clustering(),
			loadedPoints: [], // All available points (shown in map)
			selectedPoints: [], // Points selected by user (used for clustering)
			clusterColors: [],
			clusteredPoints: [],
			selectedCluster: null,
			hoveredCluster: null,

			// View Mode: 'clusters' (agrupado) or 'micro_clusters' (individual)
			viewMode: "clusters",

			// UI State
			isLoading: false,
			isLoadingClusters: false,
			showClusterList: true,
			showInfoWindow: false,
			infoWindowData: null,
			infoWindowPosition: null,
			showSelectedPointsPanel: false,
			showHeatmap: false,

			// Drawing/Selection
			isSelectingPoints: false,
			drawnPolygons: [],
			selectedPolygon: null,
			drawingManager: null,
			pointSelectionMode: "polygon",

			// Focus functionality
			focusedCluster: null,
			allClusteredPoints: null,

			// Focus state
			isFocused: false,

			// Cluster list
			allClusters: [],
			clustersPagination: {
				page: 1,
				quantity: 10,
				total: 0,
				order_sort: {added_on: "desc"},
			},
			isProcessingMap: false,
		};
	},
	computed: {
		/**
		 * Check if there are loaded points
		 */
		hasLoadedPoints() {
			return this.loadedPoints && this.loadedPoints.length > 0;
		},

		/**
		 * Check if there are selected points
		 */
		hasSelectedPoints() {
			return this.selectedPoints && this.selectedPoints.length > 0;
		},

		/**
		 * Check if clustering is ready to be created
		 */
		canCreateClustering() {
			return (
				this.currentClustering.name.length > 0 &&
				this.currentClustering.total_clusters > 0 &&
				this.hasSelectedPoints // Changed from hasLoadedPoints to hasSelectedPoints
			);
		},

		/**
		 * Get total loaded points count
		 */
		totalLoadedPoints() {
			return this.loadedPoints.length;
		},

		/**
		 * Get total selected points count
		 */
		totalSelectedPoints() {
			return this.selectedPoints.length;
		},

		/**
		 * Check if points limit is exceeded
		 */
		isPointsLimitExceeded() {
			return this.totalSelectedPoints > 500;
		},

		/**
		 * Check if there are clustered points
		 */
		hasClusters() {
			return this.clusteredPoints && this.clusteredPoints.length > 0;
		},

		/**
		 * Group clustered points by cluster_id
		 */
		groupedClusters() {
			if (!this.hasClusters) return {};
			return groupPointsByCluster(this.clusteredPoints);
		},

		/**
		 * Get cluster statistics (dynamic based on viewMode)
		 */
		clusterStatistics() {
			if (!this.currentClustering || !this.currentClustering.clusters) {
				return [];
			}
			// Dynamic: return statistics based on viewMode
			if (this.viewMode === "micro_clusters") {
				const stats = this.prepareMicroClusterStatistics();
				return stats;
			} else {
				const stats = this.prepareGroupedClusterStatistics();
				return stats;
			}
		},

		/**
		 * Get current wizard step for display in restore button
		 */
		wizardCurrentStep() {
			return this.$refs.wizard ? this.$refs.wizard.currentStep + 1 : 1;
		},
	},
	methods: {
		/**
		 * Prepare statistics for GROUPED clusters
		 */
		prepareGroupedClusterStatistics() {
			if (!this.currentClustering || !this.currentClustering.clusters) {
				return [];
			}

			return this.currentClustering.clusters.map((cluster) => {
				const stats = calculateClusterStats(cluster.points);
				return {
					id: cluster.identification,
					label: cluster.identification,
					color: cluster.color || "#999999",
					totalMicroClusters: cluster.micro_clusters ? cluster.micro_clusters.length : 0,
					points: cluster.points,
					...stats,
				};
			});
		},

		/**
		 * Prepare statistics for INDIVIDUAL micro_clusters
		 */
		prepareMicroClusterStatistics() {
			if (!this.currentClustering || !this.currentClustering.micro_clusters) {
				return [];
			}

			return this.currentClustering.micro_clusters.map((mc, index) => {
				return {
					id: mc.micro_cluster_identification,
					label: `MC-${mc.micro_cluster_identification}`,
					color: this.getUniqueColorForMicroCluster(mc, index),
					totalPoints: mc.total_points || (mc.points ? mc.points.length : 0),
					totalMicroClusters: 1,
					totalDemand: mc.total_demand || 0,
					totalCapacity: mc.total_capacity || 1,
					usagePercentage: mc.usage_percentage ? Math.round(mc.usage_percentage) : 0,
					micro_cluster_identification: mc.micro_cluster_identification,
					cluster_id: mc.cluster_identification,
					points: mc.points,
				};
			});
		},
		/**
		 * Get unique color for micro_cluster (avoid duplicates)
		 */
		getUniqueColorForMicroCluster(mc, index) {
			if (!this.currentClustering || !this.currentClustering.micro_clusters) {
				return mc.cluster_color || "#999999";
			}
			const allUsedColors = this.currentClustering.micro_clusters.map((m) => m.cluster_color);
			const colorCount = allUsedColors.filter((c) => c === mc.cluster_color).length;
			if (colorCount > 1) {
				return this.generateUniqueColor(mc.cluster_color, index, allUsedColors);
			}
			return mc.cluster_color || "#999999";
		},

		/**
		 * Generate truly unique color variation
		 */
		generateUniqueColor(baseColor, index, usedColors) {
			const hsl = this.hexToHSL(baseColor);
			let attempts = 0;
			let newColor;

			do {
				// Vary HUE with greater amplitude to avoid similar colors
				const hueShift = ((index + attempts) * 45) % 360; // 45° separation
				const saturationShift = (attempts % 3) * 15; // Vary saturation too
				const lightnessShift = Math.floor(attempts / 3) * 10; // Vary lightness

				newColor = this.hslToHex(
					(hsl.h + hueShift) % 360,
					Math.min(100, Math.max(30, hsl.s + saturationShift)),
					Math.min(80, Math.max(30, hsl.l + lightnessShift))
				);

				attempts++;
			} while (usedColors.includes(newColor) && attempts < 50);

			return newColor;
		},

		/**
		 * Convert HEX to HSL
		 */
		hexToHSL(hex) {
			const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
			if (!result) return {h: 0, s: 0, l: 0};

			let r = parseInt(result[1], 16) / 255;
			let g = parseInt(result[2], 16) / 255;
			let b = parseInt(result[3], 16) / 255;

			const max = Math.max(r, g, b);
			const min = Math.min(r, g, b);
			let h,
				s,
				l = (max + min) / 2;

			if (max === min) {
				h = s = 0;
			} else {
				const d = max - min;
				s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

				switch (max) {
					case r:
						h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
						break;
					case g:
						h = ((b - r) / d + 2) / 6;
						break;
					case b:
						h = ((r - g) / d + 4) / 6;
						break;
				}
			}

			return {h: h * 360, s: s * 100, l: l * 100};
		},

		/**
		 * Convert HSL to HEX
		 */
		hslToHex(h, s, l) {
			s /= 100;
			l /= 100;

			const c = (1 - Math.abs(2 * l - 1)) * s;
			const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
			const m = l - c / 2;

			let r = 0,
				g = 0,
				b = 0;

			if (h < 60) {
				r = c;
				g = x;
			} else if (h < 120) {
				r = x;
				g = c;
			} else if (h < 180) {
				g = c;
				b = x;
			} else if (h < 240) {
				g = x;
				b = c;
			} else if (h < 300) {
				r = x;
				b = c;
			} else {
				r = c;
				b = x;
			}

			const toHex = function (n) {
				const hex = Math.round((n + m) * 255).toString(16);
				return hex.length === 1 ? "0" + hex : hex;
			};

			return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
		},

		/**
		 * Set view mode (clusters or micro_clusters)
		 */
		async setViewMode(mode) {
			if (this.viewMode === mode) {
				return;
			}
			try {
				this.viewMode = mode;

				// Clear selection state but KEEP clustering data
				this.selectedCluster = null;
				this.hoveredCluster = null;
				this.infoWindowData = null;
				this.showInfoWindow = false;

				if (this.showStatisticsPanel !== undefined) {
					// Keep statistics panel open but refresh content
					this.showStatisticsPanel = true;
				}

				// Update visualization immediately
				this.updateDeckLayers();

				this.$vs.notify({
					color: "primary",
					title: this.$t("Information"),
					text:
						mode === "clusters"
							? this.$t("Clustering.SwitchedToClusters")
							: this.$t("Clustering.SwitchedToMicroClusters"),
					time: 2000,
				});
			} catch (error) {
				console.error("Error in setViewMode:", error);
			}
		},

		/**
		 * View clustering on map (unified method that respects viewMode)
		 */
		async viewClusteringOnMap(clustering) {
			this.isProcessingMap = true;

			try {
				this.clearSelection();
				// Always load as clusters (grouped) when selecting from the list
				await this.viewClustersOnMap(clustering);
				this.showStatisticsPanel = true;
			} catch (error) {
				console.error("Error viewing clustering:", error);
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Clustering.LoadError"),
				});
			} finally {
				this.isProcessingMap = false;
				this.showWizard = false;
			}
		},

		/**
		 * Initialize Google Maps
		 */
		async initMap() {
			if (!window.google || !window.google.maps) {
				return;
			}

			const mapElement = document.getElementById("clustering-map");
			if (!mapElement) {
				return;
			}
			this.map = await new google.maps.Map(mapElement, {
				center: this.mapCenter,
				zoom: this.mapZoom,
				mapTypeControl: true,
				streetViewControl: true,
				fullscreenControl: true,
				zoomControl: true,
			});

			this.mapLoaded = true;
			this.initDeckGL();
			this.initDrawingManager();

			// Map click listener
			this.map.addListener("click", this.onMapClick);
		},

		/**
		 * Initialize deck.gl overlay
		 */
		initDeckGL() {
			if (!this.map) {
				return;
			}

			this.deckOverlay = new GoogleMapsOverlay({
				layers: [],
			});

			this.deckOverlay.setMap(this.map);

			// Add listeners to close info window on map interactions (performance optimization)
			google.maps.event.addListener(this.map, "dragstart", () => {
				if (this.showInfoWindow) {
					this.closeInfoWindow();
				}
			});

			google.maps.event.addListener(this.map, "zoom_changed", () => {
				if (this.showInfoWindow) {
					this.closeInfoWindow();
				}
			});

			this.updateDeckLayers();
		},

		/**
		 * Initialize Google Maps Drawing Manager
		 */
		initDrawingManager() {
			if (!window.google || !this.map) {
				return;
			}

			this.drawingManager = new google.maps.drawing.DrawingManager({
				drawingMode: null,
				drawingControl: false,
				polygonOptions: {
					fillColor: "#420d91",
					fillOpacity: 0.3,
					strokeWeight: 2,
					strokeColor: "#1976D2",
					clickable: true,
					editable: true,
					zIndex: 0,
				},
			});

			this.drawingManager.setMap(this.map);

			// Listen for polygon complete
			google.maps.event.addListener(this.drawingManager, "polygoncomplete", this.onPolygonComplete);
		},

		/**
		 * Calculate convex hulls for clusters with buffer
		 */
		calculateConvexHulls() {
			const hulls = [];
			if (!this.hasClusters || !this.clusteredPoints || this.clusteredPoints.length === 0) {
				return hulls;
			}
			// Group points by cluster OR micro_cluster depending on view mode
			const clusterGroups = {};
			this.clusteredPoints.forEach((point) => {
				const groupId =
					this.viewMode === "micro_clusters" ? point.micro_cluster_id : point.cluster_identification;
				if (!clusterGroups[groupId]) {
					clusterGroups[groupId] = [];
				}
				clusterGroups[groupId].push(point);
			});

			// Calculate convex hull for each group
			Object.keys(clusterGroups).forEach((groupId) => {
				const points = clusterGroups[groupId];

				// Get color and convert to RGB if needed
				const clusterColor = points[0].cluster_color || points[0].clusterColor;
				let rgbColor;

				if (typeof clusterColor === "string") {
					// Convert hex to RGB
					rgbColor = hexToRgb(clusterColor);
				} else if (Array.isArray(clusterColor)) {
					// Already RGB
					rgbColor = clusterColor;
				} else {
					// Default gray
					rgbColor = [128, 128, 128];
				}

				if (points.length < 3) {
					// For 1-2 points, create a small buffer circle
					if (points.length === 1) {
						const p = points[0];
						const radius = 0.003; // ~300m - increased buffer
						const circle = [];
						for (let i = 0; i < 32; i++) {
							const angle = (i / 32) * 2 * Math.PI;
							circle.push([
								parseFloat(p.longitude || p.cx) + radius * Math.cos(angle),
								parseFloat(p.latitude || p.cy) + radius * Math.sin(angle),
							]);
						}
						hulls.push({
							groupId,
							polygon: circle,
							color: rgbColor,
						});
					} else if (points.length === 2) {
						// Create a rectangle around the line
						const p1 = points[0];
						const p2 = points[1];
						const lng1 = parseFloat(p1.longitude || p1.cx);
						const lat1 = parseFloat(p1.latitude || p1.cy);
						const lng2 = parseFloat(p2.longitude || p2.cx);
						const lat2 = parseFloat(p2.latitude || p2.cy);
						const buffer = 0.002; // Increased buffer

						hulls.push({
							groupId,
							polygon: [
								[lng1 - buffer, lat1 - buffer],
								[lng2 - buffer, lat2 - buffer],
								[lng2 + buffer, lat2 + buffer],
								[lng1 + buffer, lat1 + buffer],
								[lng1 - buffer, lat1 - buffer],
							],
							color: rgbColor,
						});
					}
				} else {
					// Calculate convex hull for 3+ points
					try {
						const turfPointsArray = points.map((p) => [
							parseFloat(p.longitude || p.cx),
							parseFloat(p.latitude || p.cy),
						]);
						const featureCollection = turfPoints(turfPointsArray);
						const hull = convex(featureCollection);

						if (hull && hull.geometry && hull.geometry.coordinates) {
							// Add buffer to hull coordinates
							const hullCoords = hull.geometry.coordinates[0];
							const bufferedCoords = this.addBufferToPolygon(hullCoords, 0.0015); // ~150m buffer

							hulls.push({
								groupId,
								polygon: bufferedCoords,
								color: rgbColor,
							});
						}
					} catch (error) {
						console.warn(`Error calculating convex hull for cluster ${groupId}:`, error);
					}
				}
			});

			return hulls;
		},

		/**
		 * Add buffer to polygon coordinates
		 */
		addBufferToPolygon(coords, bufferSize) {
			// Calculate centroid
			let centerLng = 0;
			let centerLat = 0;
			const validCoords = coords.slice(0, -1); // Remove duplicate last point

			validCoords.forEach(([lng, lat]) => {
				centerLng += lng;
				centerLat += lat;
			});

			centerLng /= validCoords.length;
			centerLat /= validCoords.length;

			// Expand each point away from centroid
			const bufferedCoords = validCoords.map(([lng, lat]) => {
				const dx = lng - centerLng;
				const dy = lat - centerLat;
				const distance = Math.sqrt(dx * dx + dy * dy);

				if (distance === 0) return [lng, lat];

				const ratio = (distance + bufferSize) / distance;
				return [centerLng + dx * ratio, centerLat + dy * ratio];
			});

			// Close the polygon
			bufferedCoords.push(bufferedCoords[0]);

			return bufferedCoords;
		},

		/**
		 * Update deck.gl layers
		 */
		updateDeckLayers() {
			if (!this.deckOverlay) return;

			const layers = [];

			// Add polygon layer for cluster hulls (must be before points)
			if (this.hasClusters) {
				const hulls = this.calculateConvexHulls();

				if (hulls.length > 0) {
					// Configuración diferente según modo de vista
					const isClusterMode = this.viewMode === "clusters";
					const isMicroClusterMode = this.viewMode === "micro_clusters";

					const polygonLayer = new PolygonLayer({
						id: "cluster-hulls-layer",
						data: hulls,
						getPolygon: (d) => d.polygon,
						// Fill color - más opaco en clusters, más transparente en microclusters
						getFillColor: (d) => {
							const color = Array.isArray(d.color) ? d.color : [128, 128, 128];
							const opacity = 60;
							return [...color.slice(0, 3), opacity];
						},
						// Line color - más intenso en clusters
						getLineColor: (d) => {
							const color = Array.isArray(d.color) ? d.color : [128, 128, 128];
							const opacity = isClusterMode ? 255 : 200;
							return [...color.slice(0, 3), opacity];
						},
						// Line width - más grueso en clusters, más delgado en microclusters
						getLineWidth: isClusterMode ? 5 : 3,
						lineWidthMinPixels: isClusterMode ? 3 : 2,
						lineWidthMaxPixels: isClusterMode ? 8 : 5,
						// Line dash - línea continua en clusters, punteada en microclusters
						lineDashJustified: isMicroClusterMode,
						getDashArray: isMicroClusterMode ? [3, 2] : [0, 0], // MicroClusters: línea punteada
						pickable: true,
						stroked: true,
						filled: true,

						// Interaction
						onClick: (info) => {
							if (info && info.object) {
								// Show cluster info window
								this.handleClusterPolygonClick(info);
							}
						},

						// Update triggers
						updateTriggers: {
							getFillColor: [this.viewMode],
							getLineColor: [this.viewMode],
							getLineWidth: [this.viewMode],
							getDashArray: [this.viewMode],
						},
					});
					layers.push(polygonLayer);
				}
			}

			// Add points layer
			if (this.hasClusters) {
				// Clustered points with colors from API
				const scatterLayer = new ScatterplotLayer({
					id: "clustered-points-layer",
					data: this.clusteredPoints.map((point) => {
						// Use the color from the point itself (comes from API)
						return formatPointForDeckGL(point);
					}),
					getPosition: (d) => d.position,
					getRadius: (d) => d.radius,
					getFillColor: (d) => d.color,
					pickable: true,
					radiusMinPixels: 8,
					radiusMaxPixels: 15,
					onClick: this.onPointClick,
					onHover: this.onPointHover,
					updateTriggers: {
						getFillColor: [this.clusteredPoints, this.hoveredCluster],
					},
				});
				layers.push(scatterLayer);
			} else if (this.hasLoadedPoints) {
				// Available points (gray color - not selected)
				const availablePointsIds = this.selectedPoints.map(function (p) {
					return p.id;
				});
				const unselectedPoints = this.loadedPoints.filter(function (point) {
					return !availablePointsIds.includes(point.id);
				});

				if (unselectedPoints.length > 0) {
					const unselectedLayer = new ScatterplotLayer({
						id: "unselected-points-layer",
						data: unselectedPoints.map(function (point) {
							return formatPointForDeckGL(point, "#9E9E9E");
						}),
						getPosition: (d) => d.position,
						getRadius: 8,
						getFillColor: [158, 158, 158, 200],
						pickable: true,
						radiusMinPixels: 7,
						radiusMaxPixels: 12,
						onHover: (info) => {
							const canvas = document.querySelector("#deckgl-overlay");
							if (canvas) {
								this.map.setOptions({draggableCursor: info.object ? "pointer" : "grabbing"});
							}
						},
						onClick: (info) => {
							if (info && info.object) {
								this.onPointClick(info);
							}
						},
					});
					layers.push(unselectedLayer);
				}

				// Selected points (green color)
				if (this.hasSelectedPoints) {
					const selectedLayer = new ScatterplotLayer({
						id: "selected-points-layer",
						data: this.selectedPoints.map(function (point) {
							return formatPointForDeckGL(point, "#4CAF50");
						}),
						getPosition: (d) => d.position,
						getRadius: 8,
						getFillColor: [76, 175, 80],
						pickable: true,
						radiusMinPixels: 7,
						radiusMaxPixels: 12,
						onHover: (info) => {
							const canvas = document.querySelector("#deckgl-overlay");
							if (canvas) {
								this.map.setOptions({draggableCursor: info.object ? "pointer" : "grabbing"});
							}
						},
						onClick: (info) => {
							if (info && info.object) {
								this.onPointClick(info);
							}
						},
					});
					layers.push(selectedLayer);
				}
			}

			// Add heatmap layer if enabled
			if (this.showHeatmap && (this.hasSelectedPoints || this.hasClusters)) {
				const heatmapData = this.hasClusters ? this.clusteredPoints : this.selectedPoints;

				if (heatmapData.length > 0) {
					const heatmapLayer = new HeatmapLayer({
						id: "demand-heatmap-layer",
						data: heatmapData,
						getPosition: (d) => [parseFloat(d.longitude || d.cx), parseFloat(d.latitude || d.cy)],
						getWeight: (d) => {
							const demand = parseFloat(d.demand || 0);
							return Math.max(1, demand);
						},
						radiusPixels: 60,
						intensity: 1,
						threshold: 0.03,
						colorRange: [
							[255, 255, 178, 100], // Very light yellow
							[254, 217, 118, 150], // Light yellow
							[254, 178, 76, 200], // Orange
							[253, 141, 60, 230], // Dark orange
							[240, 59, 32, 255], // Red
							[189, 0, 38, 255], // Dark red
						],
						pickable: false,
					});
					layers.push(heatmapLayer);
				}
			}

			this.deckLayers = layers;
			this.deckOverlay.setProps({layers});
		},

		/**
		 * Load points from visit points
		 */
		async loadPointsFromVisitPoints() {
			try {
				this.openLoading();

				// Get visit points from store or API
				const visitPoints = await this.$store.dispatch("moduleVisitPoint/findVisitPointPaginated", {
					order_by: ["id"],
					sort: "asc",
				});

				if (!visitPoints || !visitPoints.records || visitPoints.records.length === 0) {
					this.$vs.notify({
						color: "warning",
						title: this.$t("Clustering.NoPointsFound.Title"),
						text: this.$t("Clustering.NoPointsFound.Text"),
					});
					this.closeLoading();
					return;
				}

				// Transformar los puntos al formato interno (con información adicional)
				this.loadedPoints = visitPoints.records.map((vp) => ({
					// Información de referencia (solo para UI)
					id: vp.id,
					identification: vp.identification || null,
					address: vp.address || null,
					latitude: parseFloat(vp.cy),
					longitude: parseFloat(vp.cx),
					demand: parseInt(vp.demand),
				}));

				// Fit map to bounds
				if (this.loadedPoints.length > 0) {
					const bounds = calculateBounds(this.loadedPoints);
					if (bounds && this.map) {
						this.map.fitBounds(bounds);
					}
				}

				this.updateDeckLayers();

				this.$vs.notify({
					color: "success",
					title: this.$t("Clustering.PointsLoaded.Title"),
					text: `${this.loadedPoints.length} ${this.$t("Clustering.PointsLoaded.Text")}`,
				});

				this.closeLoading();
			} catch (error) {
				this.closeLoading();
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Clustering.ErrorLoadingPoints"),
				});
			}
		},

		/**
		 * Create clustering
		 */
		async createClustering() {
			try {
				if (!this.canCreateClustering) {
					this.$vs.notify({
						color: "warning",
						title: this.$t("Warning"),
						text: this.$t("Clustering.ValidationError"),
					});
					return;
				}

				if (this.isPointsLimitExceeded) {
					this.$vs.notify({
						color: "danger",
						title: this.$t("Error"),
						text: this.$t("Clustering.PointsLimitExceeded"),
					});
					return;
				}
				this.openLoading();
				// Reset view mode to 'clusters' and clear current state
				this.viewMode = "clusters";
				this.currentClustering = null;
				this.clusteredPoints = [];
				this.updateDeckLayers();
				// Use ONLY selected points (not all loaded points)
				const formattedPoints = this.selectedPoints.map((point) => ({
					identification: point.identification || null,
					latitude: parseFloat(point.latitude || point.cy),
					longitude: parseFloat(point.longitude || point.cx),
					demand: parseInt(point.demand),
				}));
				// Actualizar los puntos en el clustering
				this.currentClustering.points = formattedPoints;
				const payload = this.currentClustering.toPayload();
				const response = await ClusteringServices.createClustering(payload);
				if (response && response.data) {
					// Parse API response using the model
					this.currentClustering = Clustering.fromApiResponse(response.data);
					this.clusteredPoints = this.currentClustering.points || [];

					// Get unique colors from the API response
					const uniqueColors = new Set();
					if (Array.isArray(response.data)) {
						response.data.forEach((mc) => {
							if (mc.cluster_color) {
								uniqueColors.add(mc.cluster_color);
							}
						});
					}
					this.clusterColors = Array.from(uniqueColors);

					// Update deck.gl visualization
					this.updateDeckLayers();

					this.$vs.notify({
						color: "success",
						title: this.$t("Clustering.Success.Title"),
						text: this.$t("Clustering.Success.Text"),
					});
				}
				this.closeLoading();
			} catch (error) {
				this.closeLoading();
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: error.message,
				});
			}
		},

		/**
		 * Load all clusters
		 */
		async loadAllClusters() {
			try {
				this.isLoadingClusters = true;
				const response = await ClusteringServices.getClusters(
					this.currentClustering.toFetchFilterPagination(this.clustersPagination)
				);
				if (response && response.data.records) {
					this.allClusters = response.data.records;
					this.clustersPagination.total = response.data.pagination.total_records || 0;
				}
				this.isLoadingClusters = false;
			} catch (error) {
				this.isLoadingClusters = false;
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Clustering.ErrorLoadingList"),
				});
			}
		},

		/**
		 * View specific clustering
		 */
		async viewClustersOnMap(clustering) {
			try {
				this.openLoading();
				// Reset view mode to 'clusters' and clear current state
				this.viewMode = "clusters";
				this.currentClustering = null;
				this.clusteredPoints = [];
				this.updateDeckLayers();

				const response = await ClusteringServices.getClusteringById({
					id: clustering.id,
					file_type: "result",
					format: "json",
				});

				if (response && response.data) {
					// Parse API response using the model
					this.currentClustering = Clustering.fromApiResponse(response.data);
					this.clusteredPoints = this.currentClustering.points || [];
					// Get unique colors from the API response (micro_clusters have their own colors)
					const uniqueColors = new Set();
					response.data.forEach((mc) => {
						if (mc.cluster_color) {
							uniqueColors.add(mc.cluster_color);
						}
					});
					this.clusterColors = Array.from(uniqueColors);
					// Fit map to bounds
					if (this.clusteredPoints.length > 0) {
						const bounds = calculateBounds(this.clusteredPoints);
						if (bounds && this.map) {
							this.map.fitBounds(bounds);
						}
					}
					this.updateDeckLayers();
					this.$vs.notify({
						color: "success",
						title: this.$t("Success"),
						text: `${this.$t("Clustering.Loaded")}: ${this.currentClustering.total_clusters} clusters, ${
							this.currentClustering.total_micro_clusters
						} micro-clusters, ${this.clusteredPoints.length} points`,
					});
				}
				this.closeLoading();
			} catch (error) {
				this.closeLoading();
				if (
					error.error &&
					error.error.details &&
					error.error.details.message === "No files found for this clustering."
				) {
					this.$vs.notify({
						color: "primary",
						title: this.$t("Information"),
						text: this.$t("Clustering.CreatingClusterMessageBackground"),
						time: 10000,
					});
					this.closeLoading();
					return;
				}
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Clustering.ErrorLoading"),
				});
			}
		},

		/**
		 * View specific micro clusters on map
		 */

		/**
		 * Delete clustering
		 */
		async deleteClustering(clustering) {
			try {
				this.openLoading();
				await ClusteringServices.deleteClustering({id: clustering.id});
				this.clearSelection();
				this.$vs.notify({
					color: "success",
					title: this.$t("Clustering.Deleted.Title"),
					text: this.$t("Clustering.Deleted.Text"),
				});

				// Reload list
				await this.loadAllClusters();

				this.closeLoading();
			} catch (error) {
				this.closeLoading();
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Clustering.ErrorDeleting"),
				});
			}
		},

		/**
		 * Handle point click
		 */
		onPointClick(info) {
			if (!info || !info.object) return;

			this.infoWindowData = {
				clusterId: info.object.clusterId,
				point: info.object.data,
				color: this.clusterColors[info.object.clusterId] || "#999999",
			};

			// Calculate optimal position for InfoWindow
			const infoWindowWidth = 350;
			const infoWindowHeight = 400;
			const padding = 20;

			let x = info.x || window.innerWidth / 2;
			let y = info.y || window.innerHeight / 2;

			// Adjust if InfoWindow would go off-screen to the right
			if (x + infoWindowWidth + padding > window.innerWidth) {
				x = x - infoWindowWidth - padding;
			}

			// Adjust if InfoWindow would go off-screen at the bottom
			if (y + infoWindowHeight + padding > window.innerHeight) {
				y = y - infoWindowHeight - padding;
			}

			// Ensure it doesn't go off-screen to the left or top
			x = Math.max(padding, x);
			y = Math.max(padding, y);

			this.infoWindowPosition = {
				x: x,
				y: y,
			};

			this.showInfoWindow = true;
		},

		/**
		 * Handle point hover
		 */
		onPointHover(info) {
			const canvas = document.querySelector("#deckgl-overlay");
			if (canvas) {
				this.map.setOptions({draggableCursor: info.object ? "pointer" : "grabbing"});
			}
			if (!info || !info.object) {
				this.hoveredCluster = null;
				return;
			}
			this.hoveredCluster = info.object.clusterId;
		},

		/**
		 * Handle map click (optimized to close info window)
		 */
		onMapClick() {
			this.closeInfoWindow();
		},

		/**
		 * Start polygon selection (multiple polygons allowed)
		 */
		startPolygonSelection() {
			if (!this.drawingManager) return;
			this.isSelectingPoints = true;
			this.pointSelectionMode = "polygon";
			this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
		},
		/**
		 * Undo last polygon (remove only the last drawn polygon)
		 */
		undoLastPolygon() {
			if (this.drawnPolygons.length === 0) return;

			const lastPolygon = this.drawnPolygons.pop();

			// Remove from map
			lastPolygon.setMap(null);

			// Remove listeners to avoid memory leaks
			google.maps.event.clearInstanceListeners(lastPolygon);

			// Clear reference if it was selected
			if (this.selectedPolygon === lastPolygon) {
				this.selectedPolygon = null;
			}

			// Recalculate selected points from remaining polygons
			this.recalculateSelectedPoints();

			// Sync with wizard
			this.syncWithWizard();
		},

		/**
		 * Clear all polygons and deselect all points
		 */
		clearAllPolygons() {
			if (this.drawingManager) {
				this.drawingManager.setDrawingMode(null);
			}

			// Remove all drawn polygons from map
			this.drawnPolygons.forEach(function (polygon) {
				polygon.setMap(null);
				google.maps.event.clearInstanceListeners(polygon);
			});

			this.drawnPolygons = [];
			this.selectedPolygon = null;
			this.selectedPoints = [];
			this.isSelectingPoints = false;
			this.pointSelectionMode = "none";

			// Update visualization
			this.updateDeckLayers();

			// Sync with wizard
			this.syncWithWizard();
		},

		/**
		 * Clear selection but keep drawing mode active
		 */
		clearPolygonSelection() {
			this.drawnPolygons.forEach(function (polygon) {
				polygon.setMap(null);
			});
			this.drawnPolygons = [];
			this.selectedPoints = [];
			this.updateDeckLayers();

			this.$vs.notify({
				color: "warning",
				title: this.$t("Clustering.SelectionCleared.Title"),
				text: this.$t("Clustering.SelectionCleared.Text"),
			});
		},

		/**
		 * Handle polygon complete (allow multiple polygons)
		 */
		onPolygonComplete(polygon) {
			// Configure initial polygon style
			polygon.setOptions({
				fillColor: "#420d91",
				fillOpacity: 0.2,
				strokeColor: "#420d91",
				strokeWeight: 2,
				strokeOpacity: 0.4,
				editable: false,
				clickable: true,
				zIndex: 0,
			});

			// Add unique ID to polygon
			const uid = uuidv4();
			polygon.polygonId = `polygon-${uid}`;
			polygon.isSelected = false;

			// HOVER event - Highlight visually
			google.maps.event.addListener(polygon, "mouseover", () => {
				if (!polygon.isSelected) {
					polygon.setOptions({
						fillOpacity: 0.35,
						strokeWeight: 3,
						strokeOpacity: 1,
					});
				}
			});

			// MOUSEOUT event - Return to normal state
			google.maps.event.addListener(polygon, "mouseout", () => {
				if (!polygon.isSelected) {
					polygon.setOptions({
						fillOpacity: 0.2,
						strokeWeight: 2,
						strokeOpacity: 0.4,
					});
				}
			});

			// CLICK event - Select/Deselect
			google.maps.event.addListener(polygon, "click", (event) => {
				this.handlePolygonClick(polygon, event);
			});

			// DOUBLE-CLICK event - Remove directly
			google.maps.event.addListener(polygon, "dblclick", (event) => {
				event.stop(); // Prevent map zoom
				this.confirmRemovePolygon(polygon);
			});

			// Add polygon to list
			this.drawnPolygons.push(polygon);

			// Find points inside this polygon and add to selection
			const newSelectedPoints = this.loadedPoints.filter((point) => {
				const latLng = new google.maps.LatLng(point.cy || point.latitude, point.cx || point.longitude);
				return google.maps.geometry.poly.containsLocation(latLng, polygon);
			});

			// Merge with existing selected points (avoid duplicates)
			const existingIds = this.selectedPoints.map(function (p) {
				return p.id;
			});

			newSelectedPoints.forEach((point) => {
				if (!existingIds.includes(point.id)) {
					this.selectedPoints.push(point);
					existingIds.push(point.id);
				}
			});

			// Update visualization
			this.updateDeckLayers();

			// Sync with wizard if open
			this.syncWithWizard();

			// Exit drawing mode automatically for better UX
			if (this.drawingManager) {
				this.drawingManager.setDrawingMode(null);
			}
			this.isSelectingPoints = false;

			// Notify wizard to update UI state
			if (this.$refs.wizard) {
				// Use method call instead of emit to ensure state update in child component
				if (typeof this.$refs.wizard.stopDrawingMode === "function") {
					this.$refs.wizard.stopDrawingMode();
				} else {
					// Fallback for older version
					this.$refs.wizard.$emit("finish-polygon-drawing");
				}
			}

			this.$vs.notify({
				color: "success",
				title: this.$t("Clustering.PointsAdded.Title"),
				text: `+${newSelectedPoints.length} ${this.$t("Clustering.PointsAdded.Text")} | ${this.$t(
					"RouterSelection.Total"
				)}: ${this.selectedPoints.length}`,
			});
		},

		/**
		 * Handle polygon click - Select/Deselect with visual feedback
		 */
		handlePolygonClick(polygon, event) {
			// Clear previous selection if exists
			if (this.selectedPolygon && this.selectedPolygon !== polygon) {
				this.selectedPolygon.isSelected = false;
				this.selectedPolygon.setOptions({
					fillColor: "#420d91",
					fillOpacity: 0.2,
					strokeColor: "#420d91",
					strokeWeight: 2,
				});
			}

			// Toggle current selection
			polygon.isSelected = !polygon.isSelected;

			if (polygon.isSelected) {
				// Highlight as selected (red-orange color)
				polygon.setOptions({
					fillColor: "#FF5722", // Red-orange
					fillOpacity: 0.35,
					strokeColor: "#FF5722",
					strokeWeight: 4,
					strokeOpacity: 1,
				});

				this.selectedPolygon = polygon;

				// Show informative message
				this.$vs.notify({
					color: "primary",
					title: this.$t("Clustering.PolygonSelected"),
					text: this.$t("Clustering.PolygonSelectedHelp"),
					time: 3000,
				});
			} else {
				// Return to normal state
				polygon.setOptions({
					fillColor: "#420d91",
					fillOpacity: 0.2,
					strokeColor: "#420d91",
					strokeWeight: 2,
				});

				this.selectedPolygon = null;
			}
		},

		/**
		 * Confirm and remove polygon with dialog
		 */
		confirmRemovePolygon(polygon) {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Confirm"),
				text: this.$t("Clustering.ConfirmRemovePolygon"),
				acceptText: this.$t("Crud.Delete"),
				cancelText: this.$t("Cancel"),
				accept: () => {
					this.removePolygon(polygon);
				},
			});
		},

		/**
		 * Remove specific polygon object
		 */
		removePolygon(polygon) {
			// Find polygon index
			const index = this.drawnPolygons.findIndex((p) => p.polygonId === polygon.polygonId);

			if (index === -1) return;

			// Remove from map
			polygon.setMap(null);

			// Remove listeners to avoid memory leaks
			google.maps.event.clearInstanceListeners(polygon);

			// Remove from array
			this.drawnPolygons.splice(index, 1);

			// Clear reference if it was selected
			if (this.selectedPolygon === polygon) {
				this.selectedPolygon = null;
			}

			// Recalculate selected points
			this.recalculateSelectedPoints();

			// Sync with wizard
			this.syncWithWizard();

			this.$vs.notify({
				color: "warning",
				title: this.$t("Clustering.PolygonRemoved"),
				text: `${this.$t("Clustering.RemainingPolygons")}: ${this.drawnPolygons.length}`,
			});
		},

		/**
		 * Recalculate selected points based on current polygons
		 */
		recalculateSelectedPoints(silent = false) {
			this.selectedPoints = [];

			if (this.drawnPolygons.length === 0) {
				this.updateDeckLayers();

				// Sync with wizard
				this.syncWithWizard();
				return;
			}

			const selectedIds = new Set();

			this.drawnPolygons.forEach((polygon) => {
				this.loadedPoints.forEach((point) => {
					if (selectedIds.has(point.id)) return;

					const latLng = new google.maps.LatLng(point.cy || point.latitude, point.cx || point.longitude);
					if (google.maps.geometry.poly.containsLocation(latLng, polygon)) {
						this.selectedPoints.push(point);
						selectedIds.add(point.id);
					}
				});
			});

			this.updateDeckLayers();

			// Sync with wizard
			this.syncWithWizard();

			// Show notification only if not silent mode
			if (!silent) {
				this.$vs.notify({
					color: "primary",
					title: this.$t("Clustering.Updated"),
					text: `${this.selectedPoints.length} ${this.$t("Clustering.PointsSelected.Text")}`,
				});
			}
		},
		/**
		 * Select all loaded points
		 */
		selectAllPoints() {
			this.selectedPoints = [...this.loadedPoints];
			this.updateDeckLayers();

			this.$vs.notify({
				color: "success",
				title: this.$t("Clustering.AllSelected.Title"),
				text: `${this.selectedPoints.length} ${this.$t("Clustering.AllSelected.Text")}`,
			});
		},

		/**
		 * Deselect all points
		 */
		deselectAllPoints() {
			this.selectedPoints = [];
			this.updateDeckLayers();

			this.$vs.notify({
				color: "warning",
				title: this.$t("Clustering.AllDeselected.Title"),
				text: this.$t("Clustering.AllDeselected.Text"),
			});
		},

		/**
		 * Toggle selected points panel
		 */
		toggleSelectedPointsPanel() {
			this.showSelectedPointsPanel = !this.showSelectedPointsPanel;
		},

		/**
		 * Toggle cluster list
		 */
		toggleClusterList() {
			this.showClusterList = !this.showClusterList;
			this.showStatisticsPanel = false;

			// If opening cluster list, clean up any active drawing modes
			if (this.showClusterList) {
				// Stop any active drawing modes
				this.stopClickCreateMode();
				if (this.drawingManager) {
					this.drawingManager.setDrawingMode(null);
				}
				this.isSelectingPoints = false;

				// If wizard is open (minimized or not), reset it and clear points
				if (this.showWizard) {
					this.loadedPoints = [];
					this.selectedPoints = [];
					this.updateDeckLayers();

					// Reset wizard to initial state
					const wizardComponent = this.$refs.wizard;
					if (wizardComponent && typeof wizardComponent.resetWizard === "function") {
						wizardComponent.resetWizard();
					}

					// Close wizard
					this.showWizard = false;
					this.isWizardMinimized = false;
				}
			}
		},
		/**
		 * Reset clustering
		 */
		resetClustering() {
			this.currentClustering = new Clustering();
			this.clusteredPoints = [];
			this.clusterColors = [];
			this.selectedCluster = null;
			this.hoveredCluster = null;
			this.showInfoWindow = false;
			this.infoWindowData = null;
			this.updateDeckLayers();
		},

		/**
		 * Open loading indicator
		 */
		openLoading() {
			this.isLoading = true;
			this.$vs.loading();
		},
		/**
		 * Close loading indicator
		 */
		closeLoading() {
			this.isLoading = false;
			this.$vs.loading.close();
		},
		onRefreshClusters() {
			this.clustersPagination.page = 1;
			this.loadAllClusters();
		},
		/**
		 * Toggle heatmap visibility
		 */
		toggleHeatmap() {
			this.showHeatmap = !this.showHeatmap;
			this.updateDeckLayers();

			this.$vs.notify({
				color: this.showHeatmap ? "success" : "primary",
				title: this.$t("Information"),
				text: this.showHeatmap ? this.$t("Clustering.HeatmapEnabled") : this.$t("Clustering.HeatmapDisabled"),
			});
		},

		/**
		 * Zoom to specific cluster
		 * @param {Object} cluster - Cluster object or cluster ID
		 */
		zoomToCluster(cluster) {
			if (!this.map || !cluster) return;
			// Get cluster ID
			const clusterId = typeof cluster === "object" ? cluster.id : cluster;

			// Use allClusteredPoints if there's a focus active, otherwise use clusteredPoints
			const sourcePoints =
				this.focusedCluster && this.allClusteredPoints ? this.allClusteredPoints : this.clusteredPoints;

			// Find all points in this cluster
			const clusterPoints = sourcePoints.filter((point) => {
				if (this.viewMode === "micro_clusters") {
					const pointMicroClusterId = point.micro_cluster_id;
					return pointMicroClusterId === clusterId;
				} else {
					const pointClusterId = point.cluster_identification;
					return pointClusterId === clusterId;
				}
			});

			if (clusterPoints.length === 0) {
				this.$vs.notify({
					color: "warning",
					title: this.$t("Warning"),
					text: this.$t("Clustering.NoPointsInCluster"),
				});
				return;
			}

			// Calculate bounds for the cluster
			const bounds = calculateBounds(clusterPoints);

			if (bounds && this.map) {
				// Add some padding
				this.map.fitBounds(bounds, {
					padding: {top: 100, bottom: 100, left: 100, right: 100},
				});
			}
		},

		/**
		 * Focus on specific cluster/microcluster (hide others)
		 */
		focusOnCluster(cluster) {
			if (!cluster) return;

			const clusterId = typeof cluster === "object" ? cluster.id : cluster;

			// Save previous state ONLY if not already saved
			if (!this.allClusteredPoints) {
				this.allClusteredPoints = [...this.clusteredPoints];
			}

			// Filter only points from selected cluster (always from the full backup)
			if (this.viewMode === "micro_clusters") {
				this.clusteredPoints = this.allClusteredPoints.filter((point) => {
					const pointMicroClusterId = point.micro_cluster_id;
					return pointMicroClusterId === clusterId;
				});
			} else {
				this.clusteredPoints = this.allClusteredPoints.filter((point) => {
					const pointClusterId = point.cluster_identification;
					return pointClusterId === clusterId;
				});
			}

			this.focusedCluster = clusterId;
			this.isFocused = true;
			this.updateDeckLayers();
			this.zoomToCluster(cluster);
		},

		/**
		 * Clear focus and show all clusters again
		 */
		clearFocus() {
			if (this.allClusteredPoints) {
				this.clusteredPoints = [...this.allClusteredPoints];
				this.allClusteredPoints = null;
			}

			this.focusedCluster = null;
			this.isFocused = false;
			this.updateDeckLayers();
		},

		/**
		 * Clear selection and reset map
		 */
		clearSelection() {
			// Clear all points
			this.selectedPoints = [];
			this.loadedPoints = [];

			// Clear clustered points (if viewing a cluster)
			this.clusteredPoints = [];
			this.clusterColors = [];
			this.currentClustering = new Clustering();

			// Clear any drawn polygons
			this.drawnPolygons.forEach((polygon) => {
				if (polygon && polygon.setMap) {
					polygon.setMap(null);
				}
			});
			this.drawnPolygons = [];

			// Reset selection state
			this.isSelectingPoints = false;
			this.pointSelectionMode = "none";
			this.selectedCluster = null;
			this.hoveredCluster = null;

			// Update visualization
			this.updateDeckLayers();
		},

		/**
		 * Toggle Speed Dial FAB expansion
		 */
		toggleFab() {
			this.isFabExpanded = !this.isFabExpanded;
		},

		/**
		 * Handle FAB action and close the speed dial
		 */
		handleFabAction(action) {
			action();
			this.isFabExpanded = false;
		},

		/**
		 * Handle statistics panel close
		 */
		handleStatisticsPanelClose() {
			this.showStatisticsPanel = false;
		},

		/**
		 * Open statistics panel
		 */
		openStatisticsPanel() {
			this.showStatisticsPanel = true;
		},

		/**
		 * Highlight a specific cluster
		 */
		highlightCluster(stat) {
			if (this.map && stat) {
				// Update deck.gl layers to highlight the selected cluster
				this.updateDeckLayers();
			}
		},

		/**
		 * Handle cluster selection from statistics panel
		 */
		handleClusterSelect(stat) {
			this.selectedCluster = stat.id;
			this.highlightCluster(stat);
		},

		/**
		 * Handle cluster view from statistics panel
		 */
		handleClusterView(stat) {
			this.selectedCluster = stat.id;
			this.zoomToCluster(stat);
		},

		/**
		 * Handle cluster hover from statistics panel
		 */
		handleClusterHover(stat) {
			this.hoveredCluster = stat.id;
			this.highlightCluster(stat);
		},

		/**
		 * Handle cluster unhover from statistics panel
		 */
		handleClusterUnhover() {
			this.hoveredCluster = null;
		},

		/**
		 * Confirm delete clustering
		 */
		confirmDeleteClustering(clustering) {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Confirm"),
				text: `${this.$t("Clustering.ConfirmDelete")} "${clustering.name}"?`,
				accept: () => this.deleteClustering(clustering),
				acceptText: this.$t("Crud.Delete"),
				cancelText: this.$t("Cancel"),
			});
		},

		/**
		 * Handle update clustering name
		 */
		async handleUpdateClustering(data) {
			try {
				await ClusteringServices.updateClustering({
					id: data.id,
					name: data.name,
				});
				this.$vs.notify({
					color: "success",
					title: this.$t("Success"),
					text: this.$t("Clustering.Updated"),
				});
				// Refresh the clusters list
				await this.loadAllClusters();
			} catch (error) {
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Clustering.UpdateError"),
				});
			}
		},
		/**
		 * Handle cluster page change
		 */
		onClusterPageChange(page) {
			this.clustersPagination.page = page;
			this.loadAllClusters();
		},
		/**
		 * Handle view mode change (clusters vs micro_clusters)
		 */
		async handleViewModeChange(mode) {
			await this.setViewMode(mode);
		},

		/**
		 * Finish polygon drawing mode (for wizard)
		 */
		finishPolygonDrawing() {
			if (this.drawingManager) {
				this.drawingManager.setDrawingMode(null);
			}
			this.isSelectingPoints = false;

			// Update wizard with selected points
			if (this.$refs.wizard) {
				this.$refs.wizard.updateWizardData({
					selectedPoints: this.selectedPoints,
					drawnPolygons: this.drawnPolygons,
				});
			}

			this.$vs.notify({
				color: "success",
				title: this.$t("Success"),
				text: this.$t("Wizard.PolygonDrawingFinished", {
					count: this.selectedPoints.length,
				}),
			});
		},

		/**
		 * Zoom to specific point
		 */
		zoomToPoint(point) {
			if (!this.map || !point) return;

			this.map.setCenter({
				lat: point.cy || point.latitude,
				lng: point.cx || point.longitude,
			});
			this.map.setZoom(16);
		},

		/**
		 * Remove point from selection
		 */
		removePointFromSelection(point) {
			this.selectedPoints = this.selectedPoints.filter(function (p) {
				return p.id !== point.id;
			});
			this.updateDeckLayers();
		},

		/**
		 * Open wizard
		 */
		openWizard() {
			this.clearSelection();
			this.showWizard = true;
			this.showClusterList = false;
		},

		/**
		 * Close wizard
		 */
		closeWizard() {
			this.showWizard = false;
			this.isWizardMinimized = false;

			// Stop any active drawing modes
			this.stopClickCreateMode();
			if (this.drawingManager) {
				this.drawingManager.setDrawingMode(null);
			}
			this.isSelectingPoints = false;

			// Stop any active drawing modes when minimizing
			this.stopClickCreateMode();
			if (this.drawingManager) {
				this.drawingManager.setDrawingMode(null);
			}
			this.isSelectingPoints = false;
		},

		/**
		 * Minimize wizard
		 */
		minimizeWizard() {
			this.isWizardMinimized = true;

			this.$vs.notify({
				color: "primary",
				title: this.$t("Information"),
				text: this.$t("Wizard.Minimized"),
			});
		},

		/**
		 * Restore/Maximize wizard
		 */
		restoreWizard() {
			this.isWizardMinimized = false;
		},

		/**
		 * Handle export clustering - Export clustering result to Excel
		 */
		async handleExportClustering(clusteringResult) {
			try {
				if (!clusteringResult) {
					this.$vs.notify({
						color: "warning",
						title: this.$t("Warning"),
						text: this.$t("Clustering.NoDataToExport"),
					});
					return;
				}

				const response = await ClusteringServices.getClusteringById({
					id: clusteringResult.id,
					file_type: "result",
					format: "json",
				});

				let clusteredListPoints = [];
				if (response && response.data) {
					// Parse API response using the model
					this.currentClustering = Clustering.fromApiResponse(response.data);
					clusteredListPoints = this.currentClustering.points || [];
				}
				// Create workbook
				const workbook = XLSX.utils.book_new();
				// Prepare data for export
				const exportData = clusteredListPoints.map((point, index) => ({
					"#": index + 1,
					"Cluster ID": point.cluster_identification || "-",
					"Micro Cluster ID": point.micro_cluster_id || "-",
					Identificación: point.identification || "-",
					Latitud: point.latitude || point.cy,
					Longitud: point.longitude || point.cx,
					Demanda: point.demand || 0,
				}));

				// Create worksheet
				const worksheet = XLSX.utils.json_to_sheet(exportData);

				// Add worksheet to workbook
				XLSX.utils.book_append_sheet(workbook, worksheet, "Clustering");

				// Generate filename with timestamp
				const timestamp = new Date().toISOString().slice(0, 10);
				const filename = `clustering_${this.currentClustering.name || "export"}_${timestamp}.xlsx`;

				// Save file
				XLSX.writeFile(workbook, filename);

				this.$vs.notify({
					color: "success",
					title: this.$t("Success"),
					text: this.$t("Clustering.ExportedSuccessfully"),
				});
			} catch (error) {
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Clustering.ExportError"),
				});
			}
		},

		/**
		 * Handle wizard cancel - close wizard and open cluster list
		 */
		handleWizardCancel() {
			// Stop any active drawing modes
			this.stopClickCreateMode();
			if (this.drawingManager) {
				this.drawingManager.setDrawingMode(null);
			}
			this.showWizard = false;
			this.showClusterList = true;

			// Clear all loaded and selected points from map
			this.loadedPoints = [];
			this.selectedPoints = [];

			// Clear any drawn polygons
			this.drawnPolygons.forEach((polygon) => {
				if (polygon && polygon.setMap) {
					polygon.setMap(null);
				}
			});
			this.drawnPolygons = [];

			// Reset selection mode
			this.isSelectingPoints = false;
			this.pointSelectionMode = "none";

			// Update deck.gl layers to reflect cleared state
			this.updateDeckLayers();

			// Load clusters if not already loaded
			if (this.allClusters.length === 0) {
				this.loadAllClusters();
			}
		},

		/**
		 * Handle wizard view list - close wizard, clear map, and open cluster list
		 */
		handleWizardViewList() {
			this.handleWizardCancel();
		},

		/**
		 * Handle load points to map from wizard
		 */
		handleLoadPointsToMap(points) {
			// Load points to map (will render as gray/available points)
			this.loadedPoints = points;

			// Don't clear selectedPoints - they should persist
			// If wizard has selected points, sync them
			if (this.$refs.wizard && this.$refs.wizard.wizardData) {
				this.selectedPoints = this.$refs.wizard.wizardData.selectedPoints || [];
			}

			// Update deck.gl layers to show the new points
			this.updateDeckLayers();

			// Fit map to show all loaded points
			// Fit map to show all loaded points
			if (this.map && points.length > 0) {
				const bounds = new google.maps.LatLngBounds();
				let hasValidPoints = false;

				points.forEach((point) => {
					const lat = parseFloat(point.latitude || point.cy);
					const lng = parseFloat(point.longitude || point.cx);

					if (!isNaN(lat) && !isNaN(lng)) {
						bounds.extend({lat, lng});
						hasValidPoints = true;
					}
				});

				if (hasValidPoints) {
					this.map.fitBounds(bounds);
				}
			}
		},

		/**
		 * Handle sync points from wizard
		 */
		handleSyncPoints(data) {
			// Sync loadedPoints and selectedPoints from wizard to map
			if (data.loadedPoints !== undefined) {
				this.loadedPoints = data.loadedPoints;
			}
			if (data.selectedPoints !== undefined) {
				this.selectedPoints = data.selectedPoints;
			}

			// Update deck.gl layers to reflect changes
			this.updateDeckLayers();
		},

		/**
		 * Handle wizard generate clustering
		 */
		async handleWizardGenerate(wizardData) {
			try {
				// Prepare payload with wizard data
				this.currentClustering.name = wizardData.clusteringName;
				this.currentClustering.total_clusters = wizardData.totalClusters;
				this.currentClustering.total_micro_clusters = wizardData.totalMicroClusters;
				this.currentClustering.cluster_config = wizardData.cluster_config;

				// Format points from wizard selection
				const formattedPoints = wizardData.selectedPoints.map((point) => ({
					identification: point.identification || null,
					latitude: parseFloat(point.latitude || point.cy),
					longitude: parseFloat(point.longitude || point.cx),
					demand: parseInt(point.demand),
				}));

				this.currentClustering.points = formattedPoints;

				// Validate demand vs capacity
				const totalDemand = formattedPoints.reduce((sum, point) => sum + point.demand, 0);

				// Calculate total capacity from cluster configuration
				let totalCapacity = 0;
				if (wizardData.cluster_config && Array.isArray(wizardData.cluster_config)) {
					totalCapacity = wizardData.cluster_config.reduce((sum, cluster) => {
						// If cluster has micro_cluster_config, sum their capacities
						if (cluster.micro_cluster_config && Array.isArray(cluster.micro_cluster_config)) {
							const clusterCapacity = cluster.micro_cluster_config.reduce((mcSum, mc) => {
								return mcSum + (parseInt(mc.capacity) || 0);
							}, 0);
							return sum + clusterCapacity;
						}
						// Fallback: check if capacity is directly on the cluster object
						return sum + (parseInt(cluster.capacity) || 0);
					}, 0);
				}
				// Check if demand exceeds capacity
				if (totalCapacity > 0 && totalDemand > totalCapacity) {
					// Notify wizard of failure
					const wizardComponent = this.$children.find((child) => child.$options.name === "ClusteringWizard");
					if (wizardComponent) {
						wizardComponent.onGenerationFailed();
					}

					// Show error message in the format requested by user
					this.$vs.notify({
						color: "danger",
						title: this.$t("Error"),
						text: this.$t("Clustering.ErrorDemandExceedsCapacity", {
							totalDemand,
							totalCapacity,
						}),
						time: 8000,
					});
					return;
				}
				// Call clustering service directly
				const payload = this.currentClustering.toPayload();
				const response = await createClustering(payload);
				if (response && response.data) {
					const newClusteringId = response.id || response.data.id;
					this.currentClustering.name = payload.name;
					this.currentClustering.id = newClusteringId;
					// Fetch full clustering details to get points and micro-clusters
					const fullClusteringResponse = await ClusteringServices.getClusteringById({
						id: newClusteringId,
						file_type: "result",
						format: "json",
					});
					if (fullClusteringResponse && fullClusteringResponse.data) {
						// Notify wizard of success
						const wizardComponent = this.$children.find(
							(child) => child.$options.name === "ClusteringWizard"
						);
						if (wizardComponent) {
							const resultObject = {
								id: newClusteringId,
								status: "completed",
								data: fullClusteringResponse.data,
							};
							wizardComponent.onGenerationComplete(resultObject);
						}
						this.clearSelection();
						this.$vs.notify({
							color: "success",
							title: this.$t("Clustering.Success.Title"),
							text: this.$t("Clustering.Success.Text"),
						});
					}
				}
			} catch (error) {
				if (
					error.error &&
					error.error.details &&
					error.error.details.message === "No files found for this clustering."
				) {
					const resultObject = {
						id: this.currentClustering.id,
						status: "processing",
						total_clusters: this.currentClustering.total_clusters || 0,
						total_micro_clusters: this.currentClustering.total_micro_clusters || 1,
						total_points: this.currentClustering.total_points || 0,
					};

					// Notify wizard to advance to result step
					const wizardComponent = this.$children.find((child) => child.$options.name === "ClusteringWizard");
					if (wizardComponent) {
						wizardComponent.onGenerationComplete(resultObject);
					}

					this.$vs.notify({
						color: "primary",
						title: this.$t("Information"),
						text: this.$t("Clustering.CreatingClusterMessageBackground"),
						time: 10000,
					});
					this.clearSelection();
					this.closeLoading();
					return;
				}
				// Notify wizard of failure
				const wizardComponent = this.$children.find((child) => child.$options.name === "ClusteringWizard");
				if (wizardComponent) {
					wizardComponent.onGenerationFailed();
				}

				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: error.message,
				});
			}
		},
		/**
		 * Start click-to-create mode on map
		 */
		startClickCreateMode() {
			if (!this.map) return;
			this.isClickCreateMode = true;
			// Add cursor style to indicate click-to-create mode
			this.map.setOptions({draggableCursor: "crosshair"});
			// Add click listener
			this.clickCreateListener = this.map.addListener("click", (event) => {
				const lat = event.latLng.lat();
				const lng = event.latLng.lng();
				// Find the WizardStepSelection component in wizard children
				if (this.$refs.wizard) {
					const findStepComponent = (children) => {
						for (const child of children) {
							if (child.$options.name === "WizardStepSelection") {
								return child;
							}
							if (child.$children && child.$children.length > 0) {
								const found = findStepComponent(child.$children);
								if (found) return found;
							}
						}
						return null;
					};

					const stepComponent = findStepComponent(this.$refs.wizard.$children);
					if (stepComponent && typeof stepComponent.handleMapClick === "function") {
						stepComponent.handleMapClick({lat, lng});
					} else {
						// Fallback: show a simple notification
						this.$vs.notify({
							color: "warning",
							title: this.$t("Warning"),
							text: this.$t("Wizard.SelectionComponentNotFound"),
						});
					}
				}
			});

			this.$vs.notify({
				color: "success",
				title: this.$t("Information"),
				text: this.$t("Wizard.Steps.Selection.ClickCreateModeActive"),
			});
		},

		/**
		 * Stop click-to-create mode on map
		 */
		stopClickCreateMode() {
			if (!this.map) return;

			this.isClickCreateMode = false;

			// Reset cursor
			this.map.setOptions({draggableCursor: null});

			// Remove click listener
			if (this.clickCreateListener) {
				google.maps.event.removeListener(this.clickCreateListener);
				this.clickCreateListener = null;
			}
		},

		/**
		 * Sync selected points with wizard (override mixin method)
		 */
		syncWithWizard() {
			if (this.$refs.wizard && this.showWizard) {
				this.$refs.wizard.updateWizardData({
					selectedPoints: this.selectedPoints,
					drawnPolygons: this.drawnPolygons,
				});
			}
		},

		/**
		 * Close info window
		 */
		closeInfoWindow() {
			this.showInfoWindow = false;
			this.infoWindowData = null;
			this.infoWindowPosition = null;
		},

		/**
		 * Get cluster statistics for info window (optimized - no heavy calculations)
		 */
		getClusterStats(clusterId) {
			if (!this.currentClustering || clusterId === null || clusterId === undefined) {
				return null;
			}

			// Try to get from existing statistics if already calculated
			if (this.currentClustering.clusters && this.currentClustering.clusters.length > 0) {
				const cluster = this.currentClustering.clusters.find((c) => c.id === clusterId);
				if (cluster) {
					return {
						totalPoints: cluster.total_points || 0,
						center: cluster.center || {lat: 0, lng: 0},
					};
				}
			}

			// Fallback: calculate minimal stats (lightweight)
			const clusterPoints = this.clusteredPoints.filter((point) => {
				if (this.viewMode === "micro_clusters") {
					return point.micro_cluster_id === clusterId;
				}
				return point.cluster_identification === clusterId;
			});

			if (clusterPoints.length === 0) return null;

			// Calculate simple center (average)
			const sumLat = clusterPoints.reduce((sum, p) => sum + (p.latitude || p.cy), 0);
			const sumLng = clusterPoints.reduce((sum, p) => sum + (p.longitude || p.cx), 0);

			return {
				totalPoints: clusterPoints.length,
				center: {
					lat: sumLat / clusterPoints.length,
					lng: sumLng / clusterPoints.length,
				},
			};
		},

		/**
		 * Handle zoom to point from info window
		 */
		handleZoomToPointFromInfo(point) {
			if (!point) return;
			this.zoomToPoint(point);
			this.closeInfoWindow();
		},
		/**
		 * Handle cluster polygon click (show cluster/microcluster info window)
		 */
		handleClusterPolygonClick(info) {
			if (!info || !info.object || !info.object.groupId) return;
			const polygonData = info.object;
			const clusterId = polygonData.groupId;
			// Get all points in this cluster/microcluster
			const clusterPoints = this.clusteredPoints.filter((point) => {
				if (this.viewMode === "micro_clusters") {
					return point.micro_cluster_id === clusterId;
				}
				return point.cluster_identification === clusterId;
			});

			if (clusterPoints.length === 0) return;

			// Calculate cluster statistics
			const totalDemand = clusterPoints.reduce((sum, p) => sum + (p.demand || 0), 0);
			const avgDemand = totalDemand / clusterPoints.length;

			// Calculate center (average position)
			const sumLat = clusterPoints.reduce((sum, p) => sum + (p.latitude || p.cy), 0);
			const sumLng = clusterPoints.reduce((sum, p) => sum + (p.longitude || p.cx), 0);
			const centerLat = sumLat / clusterPoints.length;
			const centerLng = sumLng / clusterPoints.length;

			// Get color from polygon
			const color = Array.isArray(polygonData.color)
				? `rgb(${polygonData.color[0]}, ${polygonData.color[1]}, ${polygonData.color[2]})`
				: polygonData.color || "#999999";

			// Get cluster color from first point
			const clusterColor = clusterPoints[0].cluster_color || clusterPoints[0].clusterColor || color;

			// Create a virtual "cluster point" for the InfoWindow
			const clusterSummary = {
				id: `cluster_${clusterId}`,
				identification:
					this.viewMode === "micro_clusters"
						? `${this.$t("Clustering.MicroCluster")} ${clusterId}`
						: `${this.$t("Clustering.Cluster")} ${clusterId}`,
				cluster_identification: clusterId,
				cluster_color: clusterColor,
				latitude: centerLat,
				longitude: centerLng,
				demand: totalDemand,
				isClusterSummary: true, // Flag to identify this is a cluster, not a point
				totalPoints: clusterPoints.length,
				avgDemand: avgDemand,
				cluster_id: this.viewMode === "clusters" ? clusterId : clusterPoints[0].cluster_identification,
				micro_cluster_id: this.viewMode === "micro_clusters" ? clusterId : null,
			};

			// Show InfoWindow with cluster data
			this.infoWindowData = {
				clusterId: this.viewMode === "clusters" ? clusterId : clusterPoints[0].cluster_identification,
				point: clusterSummary,
				color: color,
				isClusterPolygon: true, // Flag to show it's from polygon click
			};

			// Calculate optimal position for InfoWindow
			// InfoWindow has ~320px width and ~350px height (approximate)
			const infoWindowWidth = 350;
			const infoWindowHeight = 400;
			const padding = 20;

			let x = info.x || window.innerWidth / 2;
			let y = info.y || window.innerHeight / 2;

			// Adjust if InfoWindow would go off-screen to the right
			if (x + infoWindowWidth + padding > window.innerWidth) {
				x = x - infoWindowWidth - padding;
			}

			// Adjust if InfoWindow would go off-screen at the bottom
			if (y + infoWindowHeight + padding > window.innerHeight) {
				y = y - infoWindowHeight - padding;
			}

			// Ensure it doesn't go off-screen to the left or top
			x = Math.max(padding, x);
			y = Math.max(padding, y);

			this.infoWindowPosition = {
				x: x,
				y: y,
			};

			this.showInfoWindow = true;
		},

		/**
		 * Handle zoom to cluster from polygon info window
		 */
		handleZoomToClusterFromPolygon(clusterId) {
			this.closeInfoWindow();
			this.zoomToCluster({id: clusterId});
		},

		/**
		 * Handle focus on cluster from polygon info window
		 */
		handleFocusOnClusterFromPolygon(clusterId) {
			this.closeInfoWindow();
			this.focusOnCluster({id: clusterId});
		},
	},
	mounted() {
		this.$nextTick(() => {
			const checkGoogleMapsInterval = setInterval(() => {
				if (window.google && window.google.maps && window.google.maps.drawing && window.google.maps.geometry) {
					clearInterval(checkGoogleMapsInterval);
					this.initMap();
				}
			}, 100);

			// Timeout de seguridad después de 10 segundos
			setTimeout(() => {
				clearInterval(checkGoogleMapsInterval);
				if (!this.mapLoaded) {
					this.$vs.notify({
						color: "danger",
						title: "Error",
						text: "No se pudo cargar Google Maps. Por favor, recargue la página.",
					});
				}
			}, 10000);
		});
	},
	beforeDestroy() {
		// Cleanup
		if (this.drawingManager) {
			this.drawingManager.setMap(null);
		}
		if (this.deckOverlay) {
			this.deckOverlay.setMap(null);
		}
		// Cleanup click create listener
		if (this.clickCreateListener) {
			google.maps.event.removeListener(this.clickCreateListener);
			this.clickCreateListener = null;
		}
	},
};
